import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PERMISSIONS, type TenantSearchHit } from '@/api/types';
import { getSession, hasPermission } from '@/auth/session';
import { AccountNotifications } from '@/components/AccountNotifications';
import { Select } from '@/components/Select';
import { Icon } from '@/components/ui/Icon';
import { useEnvironments, useLastEnvironment, writeLastEnvironment } from '@/ops/useEnvironment';
import { visibleOpsNav } from './opsNav';

/**
 * The console's frame (2026-09-30 redesign): a grouped menu with icons, ONE environment picker, a tenant search box and
 * an account menu. Below 900 px the menu becomes a drawer behind a menu button, so the console works on a tablet or a
 * phone. Pages render in the content area unchanged.
 */
export function Shell({ children }: { children: ReactNode }) {
  const session = getSession();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // A route change closes the drawer (the link was the reason it was open).
  useEffect(() => setNavOpen(false), [location.pathname]);

  const sections = visibleOpsNav(session?.user.permissions ?? []);

  return (
    <div className={`shell${navOpen ? ' nav-open' : ''}`}>
      <aside className="sidebar" id="console-nav" aria-label="Console menu">
        <div className="sidebar-brand">
          <span>Control Plane</span>
          <button type="button" className="icon-button sidebar-close" onClick={() => setNavOpen(false)} aria-label="Close the menu">
            <Icon name="close" />
          </button>
        </div>
        <nav className="sidebar-nav">
          {sections.map((section) => (
            <div key={section.key} className="nav-group" role="group" aria-label={section.title}>
              {section.items.length === 1 && section.items[0]!.label === section.title ? (
                <NavLink
                  to={section.items[0]!.to}
                  end={section.items[0]!.end}
                  className={({ isActive }) => `sidebar-link nav-top${isActive ? ' active' : ''}`}
                >
                  <Icon name={section.icon} />
                  <span>{section.title}</span>
                </NavLink>
              ) : (
                <>
                  <div className="nav-group-title">
                    <Icon name={section.icon} />
                    <span>{section.title}</span>
                  </div>
                  {section.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => `sidebar-link nav-item${isActive ? ' active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </>
              )}
            </div>
          ))}
        </nav>
      </aside>
      <div className="nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <div className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="icon-button menu-button"
              onClick={() => setNavOpen(true)}
              aria-label="Open the menu"
              aria-controls="console-nav"
              aria-expanded={navOpen}
            >
              <Icon name="menu" size={18} />
            </button>
            {hasPermission(PERMISSIONS.hostsRead) && <EnvironmentPicker />}
            {hasPermission(PERMISSIONS.analyticsRead) && <TenantSearch />}
          </div>
          <div className="topbar-right">
            {/* Account changes detected between collector runs — global state, so it lives in the header. */}
            {hasPermission(PERMISSIONS.analyticsRead) && <AccountNotifications />}
            <AccountMenu />
          </div>
        </header>

        <main className="content">{children}</main>
      </div>
    </div>
  );
}

/**
 * The ONE environment picker. Every per-environment page follows it (it is the "last picked" environment every page
 * falls back to), and a page whose link names an environment (`?env=`) is switched along with it.
 */
function EnvironmentPicker() {
  const environments = useEnvironments();
  const last = useLastEnvironment();
  const [params, setParams] = useSearchParams();
  const list = environments.data?.environments ?? [];
  if (list.length === 0) return null;

  const value = params.get('env') ?? last ?? (list.find((e) => e.enabled) ?? list[0])!.key;
  const choose = (key: string) => {
    writeLastEnvironment(key);
    if (params.has('env')) {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('env', key);
        return next;
      }, { replace: true });
    }
  };

  return (
    <Select
      label="Environment"
      value={value}
      onChange={choose}
      options={list.map((e) => ({ value: e.key, label: e.label || e.key, hint: e.enabled ? undefined : 'disabled' }))}
      inline
    />
  );
}

/**
 * Find a tenant by name, owner e-mail or id. Arrow keys move, Enter opens, Escape closes. Opens the tenant's page on its
 * environment (the account page when the replica has not placed it on one yet).
 */
export function TenantSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setQuery(text.trim()), 250);
    return () => clearTimeout(id);
  }, [text]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const results = useQuery({
    queryKey: ['tenant-search', query],
    queryFn: () => api.searchTenants(query),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
  const hits = results.data?.tenants ?? [];

  const openHit = (hit: TenantSearchHit) => {
    setOpen(false);
    setText('');
    navigate(hit.environment && hasPermission(PERMISSIONS.hostsRead)
      ? `/ops/tenants/${encodeURIComponent(hit.environment)}/${hit.tenant_id}`
      : `/accounts/${hit.tenant_id}`);
  };

  return (
    <div className="topbar-search" ref={box}>
      <Icon name="search" className="topbar-search-icon" />
      <input
        type="search"
        role="combobox"
        aria-expanded={open && query.length >= 2}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Find a tenant by name, owner e-mail or id"
        placeholder="Find a tenant…"
        value={text}
        onChange={(event) => { setText(event.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => Math.min(i + 1, Math.max(hits.length - 1, 0))); }
          if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          if (event.key === 'Enter' && hits[active]) { event.preventDefault(); openHit(hits[active]!); }
        }}
      />
      {open && query.length >= 2 && (
        <ul className="search-results" id={listId} role="listbox" aria-label="Tenants found">
          {results.isPending ? (
            <li className="search-empty t-caption muted">Searching…</li>
          ) : results.isError ? (
            <li className="search-empty t-caption note-error">The search failed — try again.</li>
          ) : hits.length === 0 ? (
            <li className="search-empty t-caption muted">No tenant matches “{query}”.</li>
          ) : (
            hits.map((hit, index) => (
              <li
                key={hit.tenant_id}
                role="option"
                aria-selected={index === active}
                className={`search-hit${index === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => { event.preventDefault(); openHit(hit); }}
              >
                <span className="t-heading">{hit.name || 'Unnamed tenant'}</span>
                <span className="t-caption muted">
                  {[hit.environment, hit.plan_code, hit.owner_email, hit.is_internal ? 'internal' : null].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/** Who is signed in, and the two things they do with it: change the password, sign out. */
function AccountMenu() {
  const navigate = useNavigate();
  const session = getSession();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const name = session?.user.display_name ?? '';
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  async function signOut() {
    await api.logout();
    navigate('/');
  }

  return (
    <div className="account-menu" ref={box}>
      <button
        type="button"
        className="account-button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${name}`}
      >
        <span className="avatar" aria-hidden="true">{initials}</span>
      </button>
      {open && (
        <div className="account-dropdown" role="menu">
          <div className="account-who">
            <strong className="t-heading">{name}</strong>
            <span className="t-caption muted">{session?.user.email}</span>
            <span className="t-caption muted">Role: {session?.user.role}</span>
          </div>
          <button type="button" role="menuitem" className="menu-row" onClick={() => { setOpen(false); navigate('/account/password'); }}>
            <Icon name="lock" /> Change password
          </button>
          <button type="button" role="menuitem" className="menu-row" onClick={() => void signOut()}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
